import React, { useEffect, useState } from "react";
import { BadgeCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface VerifiedBadgeProps {
  nhSlug: string;
  className?: string;
}

export function VerifiedBadge({ nhSlug, className = "" }: VerifiedBadgeProps) {
  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    const check = async () => {
      // Find an approved claim with an active subscription
      const { data: claims } = await supabase
        .from("nursing_home_claims")
        .select("user_id")
        .eq("nursing_home_id", nhSlug)
        .eq("status", "approved")
        .limit(1);

      if (!claims || claims.length === 0) return;

      const { data: subs } = await supabase
        .from("user_subscriptions")
        .select("id")
        .eq("user_id", claims[0].user_id)
        .in("status", ["active", "trialing"])
        .limit(1);

      if (subs && subs.length > 0) {
        setIsVerified(true);
      }
    };

    if (nhSlug) check();
  }, [nhSlug]);

  if (!isVerified) return null;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary ${className}`}
          >
            <BadgeCheck className="h-3.5 w-3.5" />
            Verified
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p className="text-xs">This nursing home is verified and actively managed by its owner</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
